/**
 * MapController - Manages Leaflet map instance and interactions
 */

import L from 'leaflet';

export class MapController {
  constructor(containerId) {
    this.containerId = containerId;
    this.map = null;
    this.gridLayer = null;
    this.legend = null;
    this.biomeService = null;
    this.gridSystem = null;
    this.apiService = null;
    this.userAnalytics = null;
    this.currentSelection = null;
    this.eventListeners = [];
  }

  /**
   * Set the biome service for accessing biome data
   */
  setBiomeService(biomeService) {
    this.biomeService = biomeService;
  }

  /**
   * Set the grid system for grid generation
   */
  setGridSystem(gridSystem) {
    this.gridSystem = gridSystem;
  }

  /**
   * Set the API service for fetching remote data when needed
   */
  setAPIService(apiService) {
    this.apiService = apiService;
  }

  /**
   * Set analytics service for tracking user interactions
   */
  setUserAnalytics(userAnalytics) {
    this.userAnalytics = userAnalytics;
  }

  /**
   * Initialize the Leaflet map
   */
  async initializeMap() {
    try {
      // Create map instance
      this.map = L.map(this.containerId, {
        worldCopyJump: true,
        minZoom: 2,
        maxZoom: 6,
        zoomSnap: 1,
        zoomDelta: 1,
        preferCanvas: true,
        maxBounds: [[-90, -180], [90, 180]],
        maxBoundsViscosity: 1.0
      }).setView([20, 0], 2);

      // Add base tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 6,
        attribution: '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors | <a href="https://landgrid.ai">LandGrid.AI</a>',
        crossOrigin: true
      }).addTo(this.map);

      // Initialize grid layer
      await this.initializeGrid();

      // Add legend
      this.addLegend();

      // Set up event handlers
      this.setupEventHandlers();

      console.log('Map initialized successfully');
    } catch (error) {
      throw new Error(`Failed to initialize map: ${error.message}`);
    }
  }

  /**
   * Initialize the biome grid overlay
   */
  async initializeGrid() {
    if (!this.gridSystem || !this.biomeService) {
      throw new Error('Grid system and biome service must be set before initializing grid');
    }

    // Create layer group for grid (feature group supports style updates)
    this.gridLayer = typeof L.featureGroup === 'function'
      ? L.featureGroup()
      : L.layerGroup();

    const gridCells = this.gridSystem.getGridCells();
    const biomeColors = this.biomeService.getBiomeColors();

    for (const cell of gridCells) {
      const biome = this.biomeService.getBiomeForCoordinates(cell.centerLat, cell.centerLon);
      const color = biomeColors[biome] || '#cccccc';

      // Create rectangle for each grid cell
      const rect = L.rectangle(cell.bounds, {
        color: '#555',
        weight: 0.5,
        fillColor: color,
        fillOpacity: 0.4,
        interactive: true
      });

      // Add data to rectangle
      rect.cellData = {
        lat: cell.centerLat,
        lon: cell.centerLon,
        biome,
        bounds: cell.bounds
      };

      // Add event handlers
      rect.on('mouseover', this.onCellHover.bind(this));
      rect.on('mouseout', this.onCellMouseOut.bind(this));
      rect.on('click', this.onCellClick.bind(this));

      // Add accessibility attributes
      rect.on('add', function() {
        if (this._path) {
          this._path.setAttribute('tabindex', '0');
          this._path.setAttribute('role', 'button');
          this._path.setAttribute('aria-label', `${biome} biome at ${cell.centerLat.toFixed(1)}, ${cell.centerLon.toFixed(1)}`);
        }
      });

      this.gridLayer.addLayer(rect);
    }

    // Add grid layer to map
    this.gridLayer.addTo(this.map);
  }

  /**
   * Handle cell hover events
   */
  onCellHover(event) {
    const rect = event.target;
    const data = rect.cellData;

    // Update visual feedback
    rect.setStyle({ fillOpacity: 0.65 });

    // Dispatch custom event
    this.dispatchCellEvent('cell:hover', data);
  }

  /**
   * Handle cell mouse out events
   */
  onCellMouseOut(event) {
    const rect = event.target;

    // Reset visual feedback
    rect.setStyle({ fillOpacity: 0.4 });

    // Dispatch custom event
    this.dispatchCellEvent('cell:mouseout', rect.cellData);
  }

  /**
   * Handle cell click events
   */
  onCellClick(event) {
    const rect = event.target;
    const data = rect.cellData;

    // Update current selection
    if (this.currentSelection) {
      this.currentSelection.setStyle({ weight: 0.5 });
    }

    this.currentSelection = rect;
    rect.setStyle({ weight: 3, color: '#2563eb' });

    if (this.userAnalytics) {
      this.userAnalytics.trackBiomeExploration?.(
        data.biome,
        { lat: data.lat, lon: data.lon },
        'click'
      );
    }

    // Dispatch custom event
    this.dispatchCellEvent('cell:select', data);
  }

  /**
   * Dispatch cell-related events
   */
  dispatchCellEvent(eventType, data) {
    const event = new CustomEvent(eventType, {
      detail: {
        lat: data.lat,
        lon: data.lon,
        biome: data.biome,
        bounds: data.bounds,
        description: this.biomeService.getBiomeDescription(data.biome),
        funFact: this.biomeService.getBiomeFunFact(data.biome)
      }
    });

    document.dispatchEvent(event);
  }

  /**
   * Set up event handlers for map interactions
   */
  setupEventHandlers() {
    // Handle keyboard navigation on grid cells
    this.map.getContainer().addEventListener('keydown', (event) => {
      if (event.target.getAttribute('role') === 'button') {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.target.click();
        }
      }
    });

    // Handle map clicks on empty areas
    this.map.on('click', (event) => {
      // Only handle if not clicking on a grid cell
      if (!event.originalEvent.target.closest('.leaflet-interactive')) {
        this.clearSelection();
      }
    });

    // Handle zoom changes
    this.map.on('zoomend', () => {
      this.updateGridVisibility();
    });
  }

  /**
   * Clear current cell selection
   */
  clearSelection() {
    if (this.currentSelection) {
      this.currentSelection.setStyle({ weight: 0.5, color: '#555' });
      this.currentSelection = null;

      // Dispatch clear event
      document.dispatchEvent(new CustomEvent('cell:clear'));
    }
  }

  /**
   * Update grid visibility based on zoom level
   */
  updateGridVisibility() {
    const zoom = this.map.getZoom();

    if (this.gridLayer) {
      if (zoom < 2) {
        this.gridLayer.setStyle({ weight: 0.3, fillOpacity: 0.3 });
      } else if (zoom >= 4) {
        this.gridLayer.setStyle({ weight: 0.8, fillOpacity: 0.5 });
      } else {
        this.gridLayer.setStyle({ weight: 0.5, fillOpacity: 0.4 });
      }
    }
  }

  /**
   * Add legend to the map
   */
  addLegend() {
    if (!this.biomeService) return;

    this.legend = L.control({ position: 'bottomleft' });

    this.legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'legend');
      const biomeColors = this.biomeService.getBiomeColors();

      div.innerHTML = '<div class="legend-title">Biome Legend</div>';

      for (const [biome, color] of Object.entries(biomeColors)) {
        div.innerHTML += `
          <div class="legend-item">
            <div class="legend-color" style="background: ${color}"></div>
            <span>${biome}</span>
          </div>
        `;
      }

      return div;
    };

    this.legend.addTo(this.map);
  }

  /**
   * Reset map view to initial state
   */
  resetView() {
    if (this.map) {
      this.map.setView([20, 0], 2);
      this.clearSelection();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    if (this.map) {
      // Slight delay to ensure container has new dimensions
      setTimeout(() => {
        this.map.invalidateSize();
      }, 100);
    }
  }

  /**
   * Get current map bounds
   */
  getBounds() {
    return this.map ? this.map.getBounds() : null;
  }

  /**
   * Get current zoom level
   */
  getZoom() {
    return this.map ? this.map.getZoom() : 2;
  }

  /**
   * Focus on a specific coordinate
   */
  focusOnCoordinate(lat, lon, zoom = 4) {
    if (this.map) {
      this.map.setView([lat, lon], zoom);
    }
  }

  /**
   * Get map instance for external use
   */
  getMapInstance() {
    return this.map;
  }

  /**
   * Destroy the map and clean up resources
   */
  destroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    this.eventListeners.forEach(listener => {
      document.removeEventListener(listener.event, listener.handler);
    });

    this.eventListeners = [];
    this.currentSelection = null;
    this.gridLayer = null;
    this.legend = null;
  }
}
